import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Typography,
  Card,
  Button,
  CircularProgress,
  Box,
  IconButton,
  Modal,
  Rating,
  useMediaQuery,
  Fade,
  Skeleton,
} from '@mui/material';
import {
  PlayCircleFilled as PlayIcon,
  Description as MaterialIcon,
  Quiz as QuizIcon,
  Assignment as AssignmentIcon,
  Feedback as FeedbackIcon,
  ArrowBack as PreviousIcon,
  ArrowForward as NextIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Confetti from 'react-confetti';
import { LoadingContext } from '../../context/LoadingContext';
import './StudentDashboard.css'; // Import CSS for animations

import {ActionButton} from '../../components/ActionButton';

import { CustomModal } from '../../components/CustomModal';
import boopSound from '../../sounds/boop.mp3';
import { bookZoomLesson } from '../../services/zoombooking';
import { loginAndGetZoomToken } from '../../services/zoombooking';
import { useNavigate } from 'react-router-dom';




// Constants for reusable values
const PRIMARY_COLOR = '#004aad';
const SECONDARY_COLOR = '#003a8c';
const BACKGROUND_COLOR = '#FFFFFF';
const BOX_SHADOW = '0px 4px 20px rgba(0, 0, 0, 0.1)';
const BORDER_RADIUS = '16px';




const StudentDashboard = () => {
  const { setLoading, loading } = useContext(LoadingContext);
  const [isLessonBooked, setIsLessonBooked] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isLessonActive, setIsLessonActive] = useState(false);
  const [isSessionEnded, setIsSessionEnded] = useState(false);
  const [showRecording, setShowRecording] = useState(false);
  const [currentLessonId, setCurrentLessonId] = useState(1);
  const [showConfetti, setShowConfetti] = useState(false);
  const audioRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [authToken, setAuthToken] = useState('');
  const navigate = useNavigate();


  // States for modals and functionality
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([
    {
      question: 'What is React?',
      options: ['A library', 'A framework', 'A programming language'],
      answer: 'A library',
    },
    {
      question: 'What is JSX?',
      options: ['A syntax extension', 'A CSS framework', 'A database'],
      answer: 'A syntax extension',
    },
    {
      question: 'What is the virtual DOM?',
      options: ['A real DOM', 'A lightweight copy of the DOM', 'A database'],
      answer: 'A lightweight copy of the DOM',
    },
  ]);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [quizScore, setQuizScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [feedbackRatings, setFeedbackRatings] = useState({
    criticalThinking: 4,
    problemSolving: 3,
    quizGrade: 5,
    attendance: 4,
  });
  const [teacherFeedback, setTeacherFeedback] = useState({
    description: 'Great job on the assignment! You demonstrated excellent problem-solving skills.',
    assignmentFeedback: 'Your submission was well-structured and met all the requirements.',
  });

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
const [selectedSlot, setSelectedSlot] = useState(null);
const [zoomLink, setZoomLink] = useState('');


  const staticSchedule = [
  { start: '2025-07-15T15:00:00', end: '2025-07-15T16:00:00' },
  { start: '2025-07-15T17:00:00', end: '2025-07-15T18:00:00' },
  { start: '2025-07-16T10:00:00', end: '2025-07-16T11:00:00' },
];


  // Simulate fetching data
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setLoading(false);
    };
    fetchData();
  }, [setLoading]);

  // Simulate a 1-hour countdown
  useEffect(() => {
    if (isLessonBooked && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (isLessonBooked && countdown === 0) {
      setIsLessonActive(true);
    }
  }, [isLessonBooked, countdown]);

  // Simulate session ending after 2 hours
  useEffect(() => {
    if (isLessonActive) {
      const sessionTimer = setTimeout(() => {
        setIsSessionEnded(true);
        setShowRecording(true);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000); // Hide confetti after 5 seconds
      }, 2 * 60 * 60 * 1000);
      return () => clearTimeout(sessionTimer);
    }
  }, [isLessonActive]);

  // Play sound effect
  const playSound = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  // Handle booking the lesson
const handleBookLesson = () => {
  setScheduleModalOpen(true);
};


  // Handle joining the lesson
const handleJoinLesson = async () => {
  try {
    const data = await bookZoomLesson(selectedSlot);
    console.log('Zoom Lesson booked successfully:', data);

    alert(`Lesson booked successfully! Join with the link: ${data.links.attendee_link}`);
    playSound(); 
    setTimeout(() => {
      navigate(`/student/zoom-session?link=${encodeURIComponent(data.links.attendee_link)}`);
      }, 100);
  } catch (err) {
    console.error('Error during lesson booking:', err);
    alert(`failed to book lesson: ${err.message}`);
    
  }
};



  // Handle previous lesson
  const handlePrevious = () => {
    setCurrentLessonId((prev) => (prev === 1 ? 1 : prev - 1));
    playSound();

  };

  // Handle next lesson
  const handleNext = () => {
    setCurrentLessonId((prev) => prev + 1);
    playSound();

  };

  // Handle file upload for task
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.zip')) {
      setUploadedFile(file);
      alert('File uploaded successfully!');
    } else {
      alert('Please upload a .zip file.');
    }
    playSound();
  };

  // Handle quiz answer selection
  const handleQuizAnswer = (answer) => {
    setSelectedAnswer(answer);
    if (answer === quizQuestions[currentQuizQuestion].answer) {
      setQuizScore((prev) => prev + 1);
    }
    playSound();

  };

  // Handle next quiz question
  const handleNextQuestion = () => {
    if (currentQuizQuestion < quizQuestions.length - 1) {
      setCurrentQuizQuestion((prev) => prev + 1);
      setSelectedAnswer('');
    } else {
      setQuizCompleted(true);
    }
    playSound();
  };

  // Reset quiz
  const resetQuiz = () => {
    setCurrentQuizQuestion(0);
    setSelectedAnswer('');
    setQuizScore(0);
    setQuizCompleted(false);
    setQuizModalOpen(false);
    playSound();
  };


const handleConfirmSlot = async (slot) => {
  setSelectedSlot(slot);
  setIsLessonBooked(true);
  setCountdown(30);
  setIsLessonActive(false);
  setShowRecording(false);

  setScheduleModalOpen(false);

  await loginAndGetZoomToken();
};

  return (
    
    <Box sx={{ backgroundColor: BACKGROUND_COLOR, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Audio element for sound effects */}
    <audio ref={audioRef}>
        <source src={boopSound} type="audio/mp3" />
        Your browser does not support the audio element.
    </audio>
      {/* Confetti Animation */}
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={200}
          gravity={0.3}
          colors={['#004aad', '#ff9800', '#4caf50']}
        />
      )}

      {/* Lesson Navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '600px',
          mb: 3,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handlePrevious}
          sx={{ color: PRIMARY_COLOR, position: isMobile ? 'static' : 'absolute', left: isMobile ? 0 : -60 }}
        >
          <PreviousIcon fontSize="large" />
        </IconButton>
        <Typography variant="h5" sx={{ mx: 2, fontFamily: 'Fredoka One', color: PRIMARY_COLOR }}>
          Lesson {currentLessonId}
        </Typography>
        <IconButton
          onClick={handleNext}
          sx={{ color: PRIMARY_COLOR, position: isMobile ? 'static' : 'absolute', right: isMobile ? 0 : -60 }}
        >
          <NextIcon fontSize="large" />
        </IconButton>
      </Box>

      {/* Upcoming Lesson Card */}
      <Card
        sx={{
          padding: 3,
          borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
          backgroundColor: BACKGROUND_COLOR,
          boxShadow: BOX_SHADOW,
          mb: 0,
          width: '100%',
          maxWidth: '600px',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': { transform: 'translateY(-5px)', boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)' },
        }}
      >
        <Typography variant="h5" sx={{ fontFamily: 'Fredoka One', color: PRIMARY_COLOR, mb: 2, textAlign: 'center' }}>
          {showRecording ? 'Lesson Recording' : 'Upcoming Lesson'}
        </Typography>

        {/* Dynamic Content */}
        {loading ? (
          // Loading Skeleton
          <Box>
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="text" sx={{ fontSize: '1.5rem', mb: 2 }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
          </Box>
        ) : !isLessonBooked ? (
          // Not Booked State
          <>
            <Box
              sx={{
                backgroundImage: 'url(https://via.placeholder.com/600x300)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
              }}
            >
              <Button
                variant="contained"
                onClick={handleBookLesson}
                sx={{
                  backgroundColor: PRIMARY_COLOR,
                  '&:hover': { backgroundColor: SECONDARY_COLOR },
                  fontSize: '1.2rem',
                  padding: '10px 30px',
                }}
              >
                Book Now
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2, textAlign: 'center', color: '#666' }}>
              Book your lesson to get started!
            </Typography>
          </>
        ) : countdown > 0 ? (
          // Countdown State
          <>
            <Box
              sx={{
                backgroundImage: 'url(https://via.placeholder.com/600x300)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={(countdown / (60 * 60)) * 100}
                  size={80}
                  thickness={4}
                  sx={{ color: PRIMARY_COLOR }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    position: 'absolute',
                    color: BACKGROUND_COLOR,
                    fontWeight: 'bold',
                    fontFamily: 'Fredoka One',
                  }}
                >
                  {Math.floor(countdown / 60)}:{countdown % 60 < 10 ? '0' : ''}
                  {countdown % 60}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" sx={{ mt: 2, textAlign: 'center', color: '#666' }}>
              Your lesson starts soon. Get ready!
            </Typography>
          </>
        ) : isLessonActive && !isSessionEnded ? (
          // Join Button State
          <>
            <Box
              sx={{
                backgroundImage: 'url(https://via.placeholder.com/600x300)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
              }}
            >
              <Button
                variant="contained"
                onClick={handleJoinLesson}
                sx={{
                  backgroundColor: PRIMARY_COLOR,
                  '&:hover': { backgroundColor: SECONDARY_COLOR },
                  fontSize: '1.2rem',
                  padding: '10px 30px',
                }}
              >
                Join Now
              </Button>
            </Box>
            <Typography variant="body1" sx={{ mt: 2, textAlign: 'center', color: '#666' }}>
              Click "Join Now" to start your lesson.
            </Typography>
          </>
        ) : showRecording ? (
          // Recording State
          <>
            <Box
              sx={{
                backgroundImage: 'url(https://via.placeholder.com/600x300)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
              }}
            >
              <IconButton
                onClick={() => alert('Playing recording...')}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': { backgroundColor: BACKGROUND_COLOR },
                }}
              >
                <PlayIcon sx={{ fontSize: 60, color: PRIMARY_COLOR }} />
              </IconButton>
            </Box>
            <Typography variant="body1" sx={{ mt: 2, textAlign: 'center', color: '#666' }}>
              The session has ended. Watch the recording here.
            </Typography>
          </>
        ) : null}
      </Card>

      {/* Other Cards */}
      <Box sx={{ width: '100%', maxWidth: '600px' }}>
        {/* Material/Reference Card */}
        <Card
          sx={{
            padding: 2,
            paddingTop: 0,
            borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
            backgroundColor: BACKGROUND_COLOR,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <ActionButton
              onClick={() => window.open('https://example.com', '_blank')}
              icon={<MaterialIcon sx={{ fontSize: 12, mr: 1, color: BACKGROUND_COLOR }} />}
              label="Material"
            
              sx={{ backgroundColor: "#050304",color: '#050304' }}
            />
            <ActionButton
              onClick={() => setQuizModalOpen(true)}
              icon={<QuizIcon sx={{ fontSize: 12, mr: 1, color: BACKGROUND_COLOR }} />}
              label="Quiz"
            />
            <ActionButton
              onClick={() => setTaskModalOpen(true)}
              icon={<AssignmentIcon sx={{ fontSize: 12, mr: 1, color: BACKGROUND_COLOR }} />}
              label="Task"
            />
            <ActionButton
              onClick={() => setFeedbackModalOpen(true)}
              icon={<FeedbackIcon sx={{ fontSize: 12, mr: 1, color: BACKGROUND_COLOR }} />}
              label="Feedback"
            />
          </Box>
        </Card>

        {/* Lesson Description Card */}
        <Card
          sx={{
            padding: 3,
            borderRadius: BORDER_RADIUS,
            backgroundColor: BACKGROUND_COLOR,
            boxShadow: BOX_SHADOW,
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: 'Fredoka One', color: PRIMARY_COLOR, mb: 2 }}>
            Lesson Description
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
            This lesson covers the fundamentals of React, including components, state, and props. By the end of this
            lesson, you will be able to build a simple React application.
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
            Duration: 1 hour | Level: Beginner
          </Typography>
        </Card>
      </Box>

      {/* Task Modal */}
      <CustomModal open={taskModalOpen} onClose={() => setTaskModalOpen(false)} title="Upload Your Task">
        <Typography variant="body2" sx={{ mb: 2 }}>
          Please upload your task in a .zip file. Ensure the file contains all necessary materials.
        </Typography>
        <input type="file" accept=".zip" onChange={handleFileUpload} />
        {uploadedFile && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Uploaded: {uploadedFile.name}
          </Typography>
        )}
      </CustomModal>

      {/* Quiz Modal */}
      <CustomModal
        open={quizModalOpen}
        onClose={() => setQuizModalOpen(false)}
        title={quizCompleted ? 'Quiz Completed!' : `Quiz Question ${currentQuizQuestion + 1}`}
      >
        {quizCompleted ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your score: {quizScore}/{quizQuestions.length}
            </Typography>
            <Button variant="contained" onClick={resetQuiz}>
              Retake Quiz
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {quizQuestions[currentQuizQuestion].question}
            </Typography>
            {quizQuestions[currentQuizQuestion].options.map((option, index) => (
              <Button
                key={index}
                variant="outlined"
                fullWidth
                sx={{
                  mb: 1,
                  color:
                    selectedAnswer === option
                      ? option === quizQuestions[currentQuizQuestion].answer
                        ? 'green'
                        : 'red'
                      : 'inherit',
                }}
                onClick={() => handleQuizAnswer(option)}
              >
                {option}
              </Button>
            ))}
            <Button
              variant="contained"
              fullWidth
              onClick={handleNextQuestion}
              disabled={!selectedAnswer}
              sx={{ mt: 2 }}
            >
              Next
            </Button>
          </>
        )}
      </CustomModal>

      {/* Feedback Modal */}
      <CustomModal open={feedbackModalOpen} onClose={() => setFeedbackModalOpen(false)} title="Teacher Feedback">
        <Typography variant="body1" sx={{ mb: 2 }}>
          {teacherFeedback.description}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Assignment Feedback: {teacherFeedback.assignmentFeedback}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography>Critical Thinking:</Typography>
          <Rating value={feedbackRatings.criticalThinking} readOnly />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Problem Solving:</Typography>
          <Rating value={feedbackRatings.problemSolving} readOnly />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Quiz Grade:</Typography>
          <Rating value={feedbackRatings.quizGrade} readOnly />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Attendance:</Typography>
          <Rating value={feedbackRatings.attendance} readOnly />
        </Box>
      </CustomModal>
      {/* Schedule Modal */}
      <CustomModal open={scheduleModalOpen} onClose={() => setScheduleModalOpen(false)} title="Choose a Time Slot">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {staticSchedule.map((slot, idx) => (
            <Button
              key={idx}
              variant="outlined"
              sx={{
                mb: 2,  
                padding: '12px 30px', 
                width: '100%',  
                maxWidth: '400px', 
                fontSize: '1.1rem', 
                backgroundColor: '#f0f0f0',  
                '&:hover': { backgroundColor: '#e0e0e0' }, 
              }}
              onClick={() => handleConfirmSlot(slot)}
            >
              {new Date(slot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
              {new Date(slot.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Button>
          ))}
        </Box>
      </CustomModal>

    </Box>
  );
};

export default StudentDashboard;